<template>
	<div class="d-flex">
		<div class="d-flex flex-column ga-3 flex-grow-1 flex-shrink-1 min-width-0 ml-n1 pl-1">
			<div class="filter-scroll-wrapper">
				<v-btn
					v-if="showProjectFilterScrollControls && !filtersAtStart"
					class="filter-scroll-button filter-scroll-left"
					variant="text"
					density="compact"
					icon="icon-chevron-left"
					@click="scrollFilters('left')" />
				<v-btn
					v-if="showProjectFilterScrollControls && !filtersAtEnd"
					class="filter-scroll-button filter-scroll-right"
					variant="text"
					density="compact"
					icon="icon-chevron-right"
					@click="scrollFilters('right')" />
				<div
					ref="filterContainer"
					class="d-flex flex-row ga-3 pt-4 pb-2 filter-container"
					style="min-height: 30px"
					:class="{
						/* LSP-19648-style fix: `scroll` (not `auto`) forces the scrollbar track to paint even
						 * when the chips already fit, which on classic always-painted-scrollbar devices
						 * (Windows touch tablets, some Android tablets) rendered a permanent, non-functional
						 * scrollbar under the filter row. `auto` still scrolls/swipes exactly the same when
						 * the chips genuinely overflow — it just stops painting a scrollbar when there's
						 * nothing to scroll. */
						'overflow-x-auto': isMobile || (isProjectsFilters && !display.lgAndUp.value),
						'with-scroll-controls': showProjectFilterScrollControls
					}">
					<GpsLocationFilter
						v-if="isProjectsFilters"
						ref="gpsLocationFilter"
						:pre-selected-latitude="searchRequest.latitude"
						:pre-selected-longitude="searchRequest.longitude"
						:disabled="selectedOrganization !== 'current'"
						@filter-coordinates="updateCoordinatesFilter">
					</GpsLocationFilter>
					<DateFilter
						v-if="initialized"
						ref="dateFilter"
						:pre-selected-date-filter="searchRequest.dateFilter ?? undefined"
						:start-date="searchRequest.startDate"
						:end-date="searchRequest.endDate"
						@filter-dates="updateDateFilter"
						@clear-dates="clearDateFilter">
						<template #button="{ props }">
							<v-btn
								:disabled="selectedOrganization !== 'current'"
								v-bind="props"
								class="filter-item"
								elevation="2"
								:class="{ active: searchRequest.startDate || searchRequest.endDate }">
								Date Created
								<v-icon v-if="dateFilter?.isFilterOpen">icon-dropdown-up</v-icon>
								<v-icon v-else>icon-dropdown-down</v-icon>
							</v-btn>
						</template>
					</DateFilter>
					<ContentTypeFilter
						v-if="showContentTypeFilter"
						ref="contentTypeFilter"
						:preselected-content-types="searchRequest.contentTypes ?? []"
						:parent-type="parentType"
						@filter-content-type="updateContentTypeFilter">
						<template #button="{ props }">
							<v-btn
								v-bind="props"
								class="filter-item"
								elevation="2"
								:class="{ active: contentTypeFilterCount > 0 }"
								:disabled="selectedOrganization !== 'current'">
								View
								<v-badge
									v-if="contentTypeFilterCount > 0"
									:content="contentTypeFilterCount"
									color="primary"
									inline
									class="filter-badge" />
								<v-icon v-if="contentTypeFilter?.isFilterOpen">icon-dropdown-up</v-icon>
								<v-icon v-else>icon-dropdown-down</v-icon>
							</v-btn>
						</template>
					</ContentTypeFilter>
					<UserFilter
						v-if="!isUserFilterHidden"
						ref="userFilter"
						:header-text="isProjectsFilters ? 'Filter by creator' : 'Filter by user'"
						:pre-selected-users="selectedUserIds"
						@filter-user="updateUserFilter">
						<template #button="{ props }">
							<v-btn
								:disabled="selectedOrganization !== 'current'"
								v-bind="props"
								class="filter-item"
								elevation="2"
								:class="{ active: selectedUserIds.length > 0 }">
								{{ userFilterLabel }}
								<v-badge
									v-if="selectedUserIds.length > 0"
									:content="selectedUserIds.length"
									color="primary"
									inline
									class="filter-badge" />
								<v-icon v-if="userFilter?.isFilterOpen">icon-dropdown-up</v-icon>
								<v-icon v-else>icon-dropdown-down</v-icon>
							</v-btn>
						</template>
					</UserFilter>
					<CustomerFilter
						v-if="!isCustomerFilterHidden"
						ref="customerFilter"
						:pre-selected-customers="searchRequest.customerIds"
						@filter-customer="updateCustomerFilter">
						<template #button="{ props }">
							<v-btn
								:disabled="selectedOrganization !== 'current'"
								v-bind="props"
								class="filter-item"
								elevation="2"
								:class="{ active: searchRequest.customerIds?.length > 0 }">
								Contact
								<v-badge
									v-if="searchRequest.customerIds?.length > 0"
									:content="searchRequest.customerIds?.length"
									color="primary"
									inline
									class="filter-badge" />
								<v-icon v-if="customerFilter?.isFilterOpen">icon-dropdown-up</v-icon>
								<v-icon v-else>icon-dropdown-down</v-icon>
							</v-btn>
						</template>
					</CustomerFilter>
					<TagFilter v-if="initialized" ref="tagFilter" :pre-selected-tags="selectedTagIds" @filter-tags="updateTagFilter">
						<template #button="{ props }">
							<v-btn
								:disabled="selectedOrganization !== 'current'"
								v-bind="props"
								class="filter-item"
								elevation="2"
								:class="{ active: searchRequest.labels?.length > 0 }">
								Tag
								<v-badge
									v-if="searchRequest.labels?.length > 0"
									:content="searchRequest.labels?.length"
									color="primary"
									inline
									class="filter-badge" />
								<v-icon v-if="tagFilter?.isFilterOpen">icon-dropdown-up</v-icon>
								<v-icon v-else>icon-dropdown-down</v-icon>
							</v-btn>
						</template>
					</TagFilter>
					<StatusFilter
						v-if="initialized && isStatusFilterVisible && !isStatusFilterHidden"
						ref="statusFilter"
						:preselected-statuses="allStatuses"
						:parent-type="parentType ?? ''"
						@filter-status="updateStatusFilter">
						<template #button="{ props }">
							<v-btn
								:disabled="selectedOrganization !== 'current'"
								v-bind="props"
								class="filter-item d-flex align-center justify-center"
								elevation="2"
								:class="{ active: allStatuses.length > 0 }">
								Status
								<v-badge
									v-if="allStatuses.length > 0"
									:content="allStatuses.length"
									color="primary"
									inline
									class="filter-badge" />
								<v-icon v-if="statusFilter?.isFilterOpen">icon-dropdown-up</v-icon>
								<v-icon v-else>icon-dropdown-down</v-icon>
							</v-btn>
						</template>
					</StatusFilter>
					<!-- Org Filter (Projects Only, hidden on contact-detail pages where org is irrelevant) -->
					<OrganizationFilter
						v-if="isProjectsFilters && !isOrganizationFilterHidden"
						ref="organizationFilter"
						:pre-selected-organization="selectedOrganization"
						:parent-type="parentType ?? ''"
						@filter-organization="updateOrganizationFilter">
						<template #button="{ props }">
							<v-btn v-bind="props" class="filter-item" elevation="2" :class="{ active: selectedOrganization !== 'current' }">
								Organization
								<v-badge v-if="selectedOrganization !== 'current'" :content="1" color="primary" inline class="filter-badge" />
								<v-icon v-if="organizationFilter?.isFilterOpen">icon-dropdown-up</v-icon>
								<v-icon v-else>icon-dropdown-down</v-icon>
							</v-btn>
						</template>
					</OrganizationFilter>
				</div>
			</div>
			<button v-if="hasFilterApplied" class="clear-filter btn-no-style d-flex" @click="clearFilters" type="button">
				Clear Filters
			</button>
		</div>
		<div class="d-flex">
			<slot name="filter-append"></slot>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { IndexableEntityType } from "@/types/search/IndexableEntityTypes";
	import { SearchRequest } from "@/types/search/SearchRequest";
	import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
	import ContentTypeFilter from "./ContentTypeFilter.vue";
	import StatusFilter from "./StatusFilter.vue";
	import CustomerFilter from "./CustomerFilter.vue";
	import UserFilter from "./UserFilter.vue";
	import TagFilter from "./TagFilter.vue";
	import DateFilter from "./DateFilter.vue";
	import { DateFilterType } from "@/types/search/DateFilterType";
	import OrganizationFilter, { OrganizationFilterType } from "./OrganizationFilter.vue";
	import constants from "@/api/constants";
	import { FilterType } from "@/types/search/FilterType";
	import GpsLocationFilter from "./GpsLocationFilter.vue";
	import { UserLocation } from "@/helpers/location";
	import { useDisplay } from "vuetify";
	import { hasFilterApplied as computeHasFilterApplied } from "./hasFilterApplied";

	const props = defineProps<{
		modelValue: SearchRequest;
		parentType: IndexableEntityType | null;
		hiddenFilters?: FilterType[];
	}>();

	const emit = defineEmits<{
		(e: "update:modelValue", value: SearchRequest): void;
	}>();

	const searchRequest = ref<SearchRequest>(props.modelValue || {});
	const allStatuses = ref<string[]>([]);
	const selectedTagIds = ref<string[]>([]);
	const selectedOrganization = ref<OrganizationFilterType>("current");
	const dateFilter = ref<InstanceType<typeof DateFilter> | null>(null);
	const userFilter = ref<InstanceType<typeof UserFilter> | null>(null);
	const customerFilter = ref<InstanceType<typeof CustomerFilter> | null>(null);
	const tagFilter = ref<InstanceType<typeof TagFilter> | null>(null);
	const contentTypeFilter = ref<InstanceType<typeof ContentTypeFilter> | null>(null);
	const statusFilter = ref<InstanceType<typeof StatusFilter> | null>(null);
	const organizationFilter = ref<InstanceType<typeof OrganizationFilter> | null>(null);
	const gpsLocationFilter = ref<InstanceType<typeof GpsLocationFilter> | null>(null);
	const initialized = ref<boolean>(false);
	const filterContainer = ref<HTMLElement | null>(null);
	const filtersScrollable = ref(false);
	const filtersAtStart = ref(true);
	const filtersAtEnd = ref(false);
	const display = useDisplay();

	const isMobile = computed(() => {
		return display.mobile.value;
	});
	const isProjectsFilters = computed(() => {
		return props.parentType === "projects" || props.parentType === "project";
	});
	const showProjectFilterScrollControls = computed(() => {
		return isProjectsFilters.value && !display.lgAndUp.value && display.mdAndUp.value && filtersScrollable.value;
	});

	watch(
		() => props.modelValue,
		() => {
			searchRequest.value = props.modelValue || {};
			initialize();
			nextTick(updateFilterScrollState);
		},
		{ deep: true }
	);

	const contentTypeFilterCount = computed(() => {
		if (!searchRequest.value.contentTypes) {
			return 0;
		}

		return searchRequest.value.contentTypes.length ?? 0;
	});

	const showContentTypeFilter = computed(() => !props.parentType || props.parentType === "conversation" || props.parentType === "project");
	const hasFilterApplied = computed(() =>
		computeHasFilterApplied(showContentTypeFilter.value ? searchRequest.value : { ...searchRequest.value, contentTypes: undefined })
	);

	onMounted(() => {
		initialize();
		nextTick(() => {
			filterContainer.value?.addEventListener("scroll", updateFilterScrollState);
			updateFilterScrollState();
			setTimeout(updateFilterScrollState, 0);
		});
		window.addEventListener("resize", updateFilterScrollState);
	});

	onBeforeUnmount(() => {
		filterContainer.value?.removeEventListener("scroll", updateFilterScrollState);
		window.removeEventListener("resize", updateFilterScrollState);
	});

	const isStatusFilterVisible = computed(() => {
		return props.parentType !== "document";
	});

	const isCustomerFilterHidden = computed(() => {
		return props.hiddenFilters?.includes("customer");
	});

	const isUserFilterHidden = computed(() => {
		return props.hiddenFilters?.includes("user");
	});

	const userFilterLabel = computed(() => {
		return "User";
	});

	// The Projects page's User filter has to ask the API the creator question
	// (createdByIds) rather than the association question (userIds) — the latter also matches projects
	// merely shared with the selected people, which is not what this User filter represents.
	const userFilterKey = computed<"createdByIds" | "userIds">(() => (isProjectsFilters.value ? "createdByIds" : "userIds"));

	// [] rather than null: UserFilter's prop defaults to [], and Vue passes an explicit null straight
	// through the default (LSP-16408).
	const selectedUserIds = computed<string[]>(() => searchRequest.value?.[userFilterKey.value] ?? []);

	const isStatusFilterHidden = computed(() => {
		return props.hiddenFilters?.includes("status");
	});

	const isOrganizationFilterHidden = computed(() => {
		return props.hiddenFilters?.includes("organization");
	});

	function initialize() {
		const model = props.modelValue || {};
		const statuses = getStatuses();
		allStatuses.value = model.statuses?.filter((x) => statuses.includes(x)) ?? [];
		selectedTagIds.value = model.labels ?? [];
		selectedOrganization.value = model.organization ?? "current";
		initialized.value = true;
		nextTick(updateFilterScrollState);
	}

	function updateFilterScrollState() {
		const container = filterContainer.value;
		if (!container) {
			return;
		}
		filtersScrollable.value = container.scrollWidth > container.clientWidth + 1;
		filtersAtStart.value = container.scrollLeft <= 1;
		filtersAtEnd.value = container.scrollLeft >= container.scrollWidth - container.clientWidth - 1;
	}

	function scrollFilters(direction: "left" | "right") {
		const container = filterContainer.value;
		if (!container) {
			return;
		}
		const scrollOffset = Math.max(180, Math.floor(container.clientWidth * 0.6));
		const targetScroll = direction === "left" ? container.scrollLeft - scrollOffset : container.scrollLeft + scrollOffset;
		container.scrollTo({ left: targetScroll, behavior: "smooth" });
	}

	function getStatuses() {
		if (props.parentType === "conversation") {
			return Object.keys(constants.CONVERSATION_STATUS).filter(
				(x) =>
					x != constants.CONVERSATION_STATUS.NotStarted &&
					x != constants.CONVERSATION_STATUS.Unread &&
					x != constants.CONVERSATION_STATUS.Delivered
			);
		} else if (props.parentType === "project") {
			return Object.keys(constants.PROJECT_STATUS);
		}

		return Object.keys(constants.FILTER_STATUS);
	}

	async function updateCustomerFilter(customers: any[]) {
		const customerIds = customers.map((customer) => customer.id);
		searchRequest.value = { ...props.modelValue, customerIds: customerIds.length > 0 ? customerIds : null };
		emit("update:modelValue", searchRequest.value);
	}

	async function updateUserFilter(users: any[]) {
		const userIds = users.map((user) => user.id);
		const selected = userIds.length > 0 ? userIds : null;
		// Write both keys, never just the active one: the API applies createdByIds and userIds as
		// separate AND clauses, so a value left behind in the inactive key would keep filtering while
		// this control shows the other key's (empty) selection.
		searchRequest.value = {
			...props.modelValue,
			createdByIds: userFilterKey.value === "createdByIds" ? selected : null,
			userIds: userFilterKey.value === "userIds" ? selected : null
		};
		emit("update:modelValue", searchRequest.value);
	}

	async function updateContentTypeFilter(contentTypes: IndexableEntityType[]) {
		searchRequest.value = { ...props.modelValue, contentTypes };
		emit("update:modelValue", searchRequest.value);
	}

	async function updateStatusFilter(statuses: string[]) {
		allStatuses.value = statuses;
		searchRequest.value = { ...props.modelValue, statuses: allStatuses.value };
		emit("update:modelValue", searchRequest.value);
	}

	async function updateTagFilter(tags: any[]) {
		selectedTagIds.value = tags.map((tag) => tag.id);
		searchRequest.value = { ...props.modelValue, labels: selectedTagIds.value };
		emit("update:modelValue", searchRequest.value);
	}

	const savedFilterState = ref<Partial<SearchRequest> | null>(null);

	async function updateOrganizationFilter(organization: OrganizationFilterType) {
		const previousOrganization = selectedOrganization.value;
		selectedOrganization.value = organization;

		if (organization === "all") {
			if (previousOrganization !== "all") {
				savedFilterState.value = {
					startDate: props.modelValue.startDate,
					endDate: props.modelValue.endDate,
					dateFilter: props.modelValue.dateFilter,
					statuses: props.modelValue.statuses,
					labels: props.modelValue.labels,
					userIds: props.modelValue.userIds,
					createdByIds: props.modelValue.createdByIds,
					customerIds: props.modelValue.customerIds,
					contentTypes: props.modelValue.contentTypes,
					latitude: props.modelValue.latitude,
					longitude: props.modelValue.longitude
				};
			}

			allStatuses.value = [];
			selectedTagIds.value = [];
			dateFilter.value?.clear();
			userFilter.value?.clear();
			customerFilter.value?.clear();
			tagFilter.value?.clear();
			statusFilter.value?.clear();
			contentTypeFilter.value?.clear();
			gpsLocationFilter.value?.clearLocationFilter();

			searchRequest.value = {
				...props.modelValue,
				organization,
				startDate: null,
				endDate: null,
				dateFilter: null,
				statuses: null,
				labels: null,
				userIds: null,
				createdByIds: null,
				customerIds: null,
				contentTypes: null,
				latitude: null,
				longitude: null
			};
			emit("update:modelValue", searchRequest.value);
		} else {
			const restored = savedFilterState.value ?? {};
			savedFilterState.value = null;
			searchRequest.value = { ...props.modelValue, ...restored, organization };
			allStatuses.value = restored.statuses ?? [];
			selectedTagIds.value = restored.labels ?? [];
			emit("update:modelValue", searchRequest.value);

			await nextTick();
			dateFilter.value?.restore();
			gpsLocationFilter.value?.restore();
			await Promise.all([userFilter.value?.restore(), customerFilter.value?.restore()]);
		}
	}

	async function updateCoordinatesFilter(coordinates: UserLocation | null) {
		if (coordinates?.latitude && coordinates?.longitude) {
			searchRequest.value = { ...props.modelValue, latitude: coordinates.latitude, longitude: coordinates.longitude };
		} else {
			searchRequest.value = { ...props.modelValue, latitude: null, longitude: null };
		}
		emit("update:modelValue", searchRequest.value);
	}

	async function updateDateFilter(startDate: moment.Moment, endDate: moment.Moment, dateFilter: DateFilterType) {
		searchRequest.value = {
			...props.modelValue,
			startDate: startDate.toDate(),
			endDate: endDate.toDate(),
			dateFilter: dateFilter
		};
		emit("update:modelValue", searchRequest.value);
	}

	async function clearDateFilter() {
		searchRequest.value = {
			...props.modelValue,
			startDate: null,
			endDate: null,
			dateFilter: null
		};
		emit("update:modelValue", searchRequest.value);
	}

	const nonClearableFilters = ["tab", "searchTerm"];
	function clearFilters() {
		if (!hasFilterApplied.value) {
			return;
		}

		const backupSearchRequest: Record<string, string> = {};
		for (const key of nonClearableFilters) {
			if (key in searchRequest.value && searchRequest.value[key as keyof SearchRequest]) {
				backupSearchRequest[key] = (searchRequest.value[key as keyof SearchRequest] as string) || "";
			}
		}
		// Keep the search term when clear filters is clicked.
		searchRequest.value = { organization: "current", ...backupSearchRequest } as SearchRequest;

		allStatuses.value = [];
		selectedTagIds.value = [];
		customerFilter.value?.clear();
		userFilter.value?.clear();
		tagFilter.value?.clear();
		statusFilter.value?.clear();
		contentTypeFilter.value?.clear();
		organizationFilter.value?.reset();
		gpsLocationFilter.value?.clearLocationFilter();
		emit("update:modelValue", searchRequest.value);
	}
</script>
<style lang="scss">
	.min-width-0 {
		min-width: 0;
	}

	.filter-scroll-wrapper {
		position: relative;
		min-width: 0;
		width: 100%;
	}

	.filter-scroll-button {
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		z-index: 2;
		min-width: 28px !important;
		width: 28px;
		height: 28px;
		background: rgba(255, 255, 255, 0.95);
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
	}

	.filter-scroll-left {
		left: -6px;
	}

	.filter-scroll-right {
		right: -6px;
	}

	.filter-badge {
		letter-spacing: 1px;
		line-height: 12px;
		font-size: 8px;
	}

	button.clear-filter {
		width: fit-content;
		font-size: 0.75rem;
		font-weight: 500;
		color: var(--color-primary);
	}

	.filter-item.v-btn {
		height: 32px;
		border-radius: 1rem;
		background-color: var(--filter-inactive) !important;
		font-size: 0.75rem !important;
		font-weight: 500 !important;
	}

	.filter-item.active {
		color: var(--color-primary) !important;
		background-color: var(--filter-active) !important;
		&.v-btn--disabled {
			color: rgba(0, 0, 0, 0.26) !important;
		}
	}

	.filter-item .v-icon {
		font-size: 1rem;
	}

	.filter-item .badge {
		display: flex;
		width: 20px;
		height: 20px;
		line-height: 20px;
		font-size: 10px;
		background-color: var(--color-primary);
		color: white;
		border-radius: 50%;
		justify-content: center;
		align-items: center;
		text-align: center;
		padding-right: 2px;
	}

	/* Ensure filter buttons have breathing room on mobile when scrolling */
	.filter-container.overflow-x-auto {
		scrollbar-width: thin;

		.filter-item:first-child {
			margin-left: 2px;
		}

		.filter-item:last-child {
			margin-right: 2px;
		}
	}

	.filter-container.with-scroll-controls {
		padding-left: 28px;
		padding-right: 28px;
		/* This variant already insets the chips 28px to clear its own scroll arrows, which is far
		 * more room than a shadow needs, so it does not want the gutter's negative margin below —
		 * that would drag the chips 4px left and eat the clearance from the left arrow. */
		margin-left: 0;
	}

	/*
	 * LSP-19642: this strip scrolls horizontally, and a scroll container clips at its padding box.
	 * box-shadow never contributes to scrollable overflow, so the first chip's elevation shadow was
	 * sliced flat against the left edge ("Current Location" is always first). Give the shadows a
	 * gutter on both ends. The left gutter is cancelled by an equal negative margin so the chips
	 * still line up with the search field above them — the reporter's point that a bare padding-left
	 * is not the fix. Width is left to auto: with width: 100% the negative margin would push the
	 * strip 4px past its parent's right edge instead of only shifting its left edge outward.
	 */
	.filter-container {
		overflow-x: auto;
		margin-left: -4px;
		padding-left: 4px;
		padding-right: 4px;
	}
</style>
